/**
 * Loading Screen Component
 * 
 * Displays the Gold Machine logo with a subtle shimmer animation.
 * Used for page loading states and suspense fallbacks.
 */

import React from 'react';
import { cn } from '@/lib/utils';
import { useLogoSrc } from '@/hooks/useLogoSrc';

interface LoadingScreenProps {
  /** Additional CSS classes */
  className?: string;
  /** Size variant - 'sm' for inline, 'md' for sections, 'lg' for full page */
  size?: 'sm' | 'md' | 'lg';
  /** If true, takes full viewport height */
  fullScreen?: boolean;
}

export const LoadingScreen: React.FC<LoadingScreenProps> = ({
  className,
  size = 'lg',
  fullScreen = true,
}) => {
  const logoSrc = useLogoSrc();
  const logoSizes = {
    sm: 48,
    md: 80,
    lg: 120,
  };

  const logoSize = logoSizes[size];

  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center bg-background',
        fullScreen ? 'fixed inset-0 z-50' : 'w-full py-12',
        className
      )}
    >
      {/* Simple logo with gentle pulse */}
      <div className="relative">
        {/* Soft glow behind logo */}
        <div 
          className="absolute inset-0 blur-2xl opacity-30"
          style={{
            background: 'radial-gradient(circle, rgba(251,191,36,0.4) 0%, transparent 70%)',
            width: logoSize * 2,
            height: logoSize * 2,
            left: -logoSize / 2,
            top: -logoSize / 2,
          }}
        />
        
        {/* Logo - maintain aspect ratio */}
        <img
          src={logoSrc}
          alt="Gold Machine"
          width={logoSize}
          height={logoSize}
          className="relative z-10 animate-pulse object-contain"
          style={{ 
            width: logoSize, 
            height: logoSize,
            animationDuration: '2s',
          }}
        />
      </div>

      {/* Minimal loading indicator - just 3 dots */}
      <div className="flex items-center gap-1 mt-6">
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            className="w-1.5 h-1.5 rounded-full bg-amber-500/60"
            style={{
              animation: 'dot-pulse 1.2s infinite ease-in-out',
              animationDelay: `${i * 0.15}s`,
            }}
          />
        ))}
      </div>

      <style>{`
        @keyframes dot-pulse {
          0%, 80%, 100% {
            opacity: 0.3;
            transform: scale(0.8);
          }
          40% {
            opacity: 1;
            transform: scale(1);
          }
        }
      `}</style>
    </div>
  );
};

export default LoadingScreen;
